import { CookieConsent } from "@trustpilot/businessapp-browserstorage";
import { PatternLibraryProvider, ThemeProvider } from "@trustpilot/businessapp-patternlibrary";
import { ConstellationProvider, Locales } from "@trustpilot/constellation-components";
import { LocalizationProvider } from "ilenia";
import React, { useEffect, useLayoutEffect } from "react";
import { BrowserRouter } from "react-router-dom";

import { getShellData, getShellSecondaryData, defaultData } from "./api";
import { isBusinessUnitSelectorOpened, isMediumWidthViewport } from "./components/helpers";
import Shell from "./components/shell";
import ShellNoBusinessUnit from "./components/shellNoBusinessUnit";
import { ErrorBoundary } from "./components/system";
import Routes from "./routes";
import { ExtendedBaseDataResponseBody, BusinessAppData } from "./types";
import { AnalyticsProvider } from "./utils/analytics";
import { ISSSOAUTHENTICATED_COOKIE, SSOAUTHENTICATED_RECENTLY_COOKIE, getCookieByName } from "./utils/cookies";
import { isTrustpilotEmployee } from "./utils/employeePermissions";
import { shellSentryConfig, initializeSentry } from "./utils/helpers";
import { makeLocaleLocalizationProviderOverride } from "./utils/locale";
import { logout } from "./utils/logout";
import { getTranslations } from "./utils/translations";

const App: React.FC<ExtendedBaseDataResponseBody & { children?: React.ReactElement }> = ({
  businessUser,
  businessUnit,
  salesforceId,
  locale,
  launchDarkly,
  children,
}) => {
  const [hasAppError, setHasAppError] = React.useState<boolean>(false);
  const [data, setData] = React.useState<
    BusinessAppData & {
      shouldFetchShellSecondaryData: boolean;
      fetchedShellData: boolean;
    }
  >({
    // App data:
    businessUser,
    businessUnit,
    salesforceId,
    locale,
    launchDarkly,

    // Shell data:
    ...defaultData.appShellData,

    // Shell secondary data:
    ...defaultData.appShellSecondaryData,

    shouldFetchShellSecondaryData: false,
    fetchedShellData: false,
  });

  const isMediumViewport = isMediumWidthViewport();
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(isMediumViewport);
  const [showSubMenu, setShowSubMenu] = React.useState<boolean>(false);

  useEffect(() => {
    initializeSentry(businessUser.id, shellSentryConfig);
  });

  // trick to always get latest value of showSubMenu variable inside useLayoutEffect
  // without re-running useLayoutEffect on every showSubMenu update
  const showSubMenuRef = React.useRef(showSubMenu);
  useEffect(() => {
    showSubMenuRef.current = showSubMenu;
  });

  useLayoutEffect(() => {
    function updateSize() {
      if (!isBusinessUnitSelectorOpened() && !showSubMenuRef.current) {
        setIsCollapsed(isMediumWidthViewport());
      }
    }
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const setCollapsed = (value) => {
    if (isMediumWidthViewport()) {
      setIsCollapsed(value);
    }
  };

  const setShowSubmenu = (value, ignoreCollapse = false) => {
    if (!ignoreCollapse) {
      setCollapsed(!value);
    }

    const isSsoSamlAuthenticated = getCookieByName(ISSSOAUTHENTICATED_COOKIE);
    if (isSsoSamlAuthenticated === "true") {
      const isSsoRecentlyAuthenticated = getCookieByName(SSOAUTHENTICATED_RECENTLY_COOKIE);
      if (isSsoRecentlyAuthenticated !== "true") {
        return logout({}, "/sso-login");
      }
    }

    setShowSubMenu(value);
  };

  React.useEffect(() => {
    CookieConsent.init({ locale: locale === "la-LA" ? "en-US" : locale });
  }, [locale]);

  React.useEffect(() => {
    async function fetchShellData() {
      if (businessUnit) {
        const isSsoSamlAuthenticated = getCookieByName(ISSSOAUTHENTICATED_COOKIE);
        const appshellData = await getShellData({
          businessUnit: {
            id: businessUnit.id,
            country: businessUnit.country,
            identifyingName: businessUnit.name.identifying,
            hasAccountManagementConsent: businessUnit.hasAccountManagementConsent,
            permissions: businessUnit.access.permissions,
            displayName: businessUnit.displayName,
          },
          isSsoSamlAuthenticated: isSsoSamlAuthenticated === "true",
        });

        setData({
          ...data,
          ...appshellData,
          shouldFetchShellSecondaryData: true,
        });
      }
    }

    fetchShellData();
  }, []);

  // Renders data that should not prevent the appshell from rendering.
  React.useEffect(() => {
    async function fetchShellSecondaryData() {
      if (data.shouldFetchShellSecondaryData) {
        const appshellSecondaryData = await getShellSecondaryData({
          businessUnitId: businessUnit.id,
          locale,
        });

        setData({
          ...data,
          ...appshellSecondaryData,
          fetchedShellData: true,
        });
      }
    }

    fetchShellSecondaryData();
  }, [data.shouldFetchShellSecondaryData]);

  // Used to enable Crowdin in-context tool
  const localeLocalizationProviderOverride = makeLocaleLocalizationProviderOverride(
    locale,
    isTrustpilotEmployee(businessUser.privileges, businessUser.email),
  );

  return (
    <LocalizationProvider
      translations={getTranslations(localeLocalizationProviderOverride)}
      locale={localeLocalizationProviderOverride}
    >
      <PatternLibraryProvider locale={locale}>
        <ThemeProvider>
          <AnalyticsProvider businessUser={businessUser} businessUnit={businessUnit} locale={locale}>
            <BrowserRouter>
              {businessUnit ? (
                <>
                  <Shell
                    {...data}
                    hasAppError={hasAppError}
                    showSubMenu={showSubMenu}
                    setShowSubMenu={setShowSubmenu}
                    isCollapsed={isCollapsed}
                    setIsCollapsed={setCollapsed}
                  />
                </>
              ) : (
                <React.Fragment>
                  <ShellNoBusinessUnit businessUser={businessUser} hasAppError={hasAppError} />
                </React.Fragment>
              )}
              <main className={`businessapp__wrapper ${showSubMenu && "businessapp__wrapper--active-nav"}`}>
                <ConstellationProvider locale={locale as Locales}>
                  <ErrorBoundary hasAppError={hasAppError} setHasAppError={setHasAppError}>
                    <Routes
                      locale={locale}
                      localeLocalizationProviderOverride={localeLocalizationProviderOverride}
                      businessUser={businessUser}
                      businessUnit={businessUnit}
                      launchDarkly={launchDarkly}
                      componentOverride={children} // Used when running a microsite locally
                    />
                  </ErrorBoundary>
                </ConstellationProvider>
              </main>
            </BrowserRouter>
          </AnalyticsProvider>
        </ThemeProvider>
      </PatternLibraryProvider>
    </LocalizationProvider>
  );
};

export default App;
